import React from 'react';
import { GlobeIcon, LinkedinIcon, MapPinIcon, NewspaperIcon, SendIcon } from 'lucide-react';
import { NetworkingGuest } from '../../data/networking-guests';
import { GOLD, NetworkingCopy } from '../../data/networking-copy';
function initialsOf(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}
const linkChip = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold text-white/75 border border-white/15 transition-colors hover:border-white/35 hover:text-white';

/**
 * One guest in the directory. Memoized so typing in search re-renders only the
 * input, not the whole roster.
 */
export const GuestCard = React.memo(function GuestCard({
  guest,
  c,
  priority,
  onRequest
}: {
  guest: NetworkingGuest;
  c: NetworkingCopy;
  /** Cards in the first visible row load their portrait immediately. */
  priority: boolean;
  onRequest: (guest: NetworkingGuest) => void;
}) {
  return <article className="group flex flex-col h-full rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden transition-colors duration-200 hover:border-[#c9a227]/40 hover:bg-white/[0.05]" style={priority ? undefined :
  // Skip layout and paint for cards that are scrolled out of view.
  {
    contentVisibility: 'auto',
    containIntrinsicSize: '520px'
  }} data-id="element-2912" data-mp="3rn6xf9igw0v7">
      {/* Portrait fills the top of the card so faces read at a glance */}
      <div className="relative w-full aspect-square bg-white/[0.06] overflow-hidden" data-id="element-2913" data-mp="acz6b5ejw31d">
        {guest.photo ? <img src={guest.photo} alt={guest.name} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'low'} decoding="async" width={640} height={640} className="absolute inset-0 w-full h-full object-cover object-[center_22%]" data-id="element-2914" data-mp="o4403qv8m13s" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-2915" data-mp="1088657q6e3l1">
            <span className="font-bebas text-5xl tracking-wider text-white/25" data-id="element-2916" data-mp="156t0iqiqxt1k">
              {initialsOf(guest.name)}
            </span>
          </div>}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5" data-id="element-2917" data-mp="1a4l086pmujgb">
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#0b1f3f]" style={{
          backgroundColor: GOLD
        }} data-id="element-2918" data-mp="1exnvcja260ym">
            {guest.sector}
          </span>
          {guest.secondarySector && <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-[#0b1f3f]" style={{
          backgroundColor: GOLD
        }} data-id="element-2919" data-mp="1swncl5v8rdkr">
              {guest.secondarySector}
            </span>}
        </div>
        {guest.boardOrder !== undefined && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-white bg-[#0b1f3f]/85 ring-1 ring-white/20" data-id="element-2920" data-mp="3iigyi0kedz6">
            {c.boardBadge}
          </span>}
      </div>

      <div className="p-6 flex-1 flex flex-col" data-id="element-2921" data-mp="3naj196l0km0y">
        <h3 className="font-bebas text-3xl tracking-wider text-white leading-none" data-id="element-2922" data-mp="3icpx0j39u23b">
          {guest.name}
        </h3>
        {guest.title && <p className="mt-2 text-sm font-semibold text-white/80 leading-snug" data-id="element-2923" data-mp="3dcbmmue76g24">
            {guest.title}
          </p>}
        {guest.organization && <p className="text-sm text-white/50 leading-snug" data-id="element-2924" data-mp="9yqxx8l060ll">
            {guest.organization}
          </p>}
        {guest.location && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-white/45" data-id="element-2925" data-mp="50fnowqspo2u">
            <MapPinIcon className="w-3.5 h-3.5" data-id="element-2926" data-mp="1maz6jc213q" />
            {guest.location}
          </p>}

        {guest.bio && <p className="mt-4 text-sm leading-relaxed text-white/65" data-id="element-2927" data-mp="2kq0k3e71652o">
            {guest.bio}
          </p>}

        {guest.lookingFor && <div className="mt-5 rounded-xl border border-[#c9a227]/20 bg-[#c9a227]/[0.07] px-4 py-3" data-id="element-2928" data-mp="dczdh0710lq2">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{
          color: GOLD
        }} data-id="element-2929" data-mp="i6tufvv8ymlg">
              {c.lookingFor}
            </p>
            <p className="text-sm leading-relaxed text-white/75" data-id="element-2930" data-mp="8fz34d358wt5">
              {guest.lookingFor}
            </p>
          </div>}

        <div className="mt-auto pt-6" data-id="element-2931" data-mp="1144o90y40nti">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2932" data-mp="1133f520nmbef">
            {guest.linkedin && <a href={guest.linkedin} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2933" data-mp="m5u8z26oz32n">
                <LinkedinIcon className="w-3.5 h-3.5" data-id="element-2934" data-mp="36wl6a4qqqdn3" />
                {c.linkedin}
              </a>}
            {guest.website && <a href={guest.website} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2935" data-mp="3bxh90ezcvtqx">
                <GlobeIcon className="w-3.5 h-3.5" data-id="element-2936" data-mp="1oyybnvkkjhm4" />
                {c.website}
              </a>}
            {guest.feature && <a href={guest.feature} target="_blank" rel="noopener noreferrer" className={linkChip} data-id="element-2937" data-mp="1yxnt5n8jz01u">
                <NewspaperIcon className="w-3.5 h-3.5" data-id="element-2938" data-mp="1b5p6y87kfk9x" />
                {c.feature}
              </a>}
          </div>
          {guest.acceptsIntros !== false && <button onClick={() => onRequest(guest)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.01]" style={{
          backgroundColor: GOLD
        }} data-id="element-2939" data-mp="1k1oeftqxu1nq">
              <SendIcon className="w-4 h-4" data-id="element-2940" data-mp="ngo3z0hi51c1" />
              {c.requestIntro}
            </button>}
        </div>
      </div>
    </article>;
});